import { z } from 'zod';
import { Email, IsoDateTime, Uuid } from './common.js';

export const Role = z.enum(['ADMIN', 'MEMBER']);
export type Role = z.infer<typeof Role>;

export const UserStatus = z.enum(['INVITED', 'ACTIVE', 'DISABLED']);
export type UserStatus = z.infer<typeof UserStatus>;

/** Jeton de couleur de la pastille (variables CSS `--p-m`, `--p-s`, `--p-p`, `--p-i` de la maquette). */
export const UserColor = z.enum(['p-m', 'p-s', 'p-p', 'p-i']);
export type UserColor = z.infer<typeof UserColor>;

const Name = z.string().trim().min(1, 'Nom obligatoire').max(80);
const Initials = z
  .string()
  .trim()
  .min(1, 'Initiale obligatoire')
  .max(2, 'Deux caractères au plus')
  .transform((value) => value.toUpperCase());

export const User = z.object({
  id: Uuid,
  email: z.string(),
  name: z.string(),
  initials: z.string(),
  color: UserColor,
  role: Role,
  status: UserStatus,
  createdAt: IsoDateTime,
  /**
   * Lot S : dernière connexion (null : jamais connecté depuis le lot S, ou jamais du tout). `hasLoggedIn` vaut
   * vrai dès qu'une connexion est connue (compte Google relié, connexion enregistrée) : l'adresse d'un membre
   * qui ne s'est jamais connecté se corrige, et il peut être fusionné avec un autre. Valeurs par défaut : une
   * réponse d'avant le lot S reste lisible.
   */
  lastLoginAt: IsoDateTime.nullable().default(null),
  hasLoggedIn: z.boolean().default(false),
});
export type User = z.infer<typeof User>;

/** `GET /api/admin/users` : tableau brut (non paginé). */
export const AdminUserList = z.array(User);
export type AdminUserList = z.infer<typeof AdminUserList>;

/** `POST /api/admin/users` : invitation (la personne se connecte ensuite avec son compte Google). */
export const InviteUserBody = z.object({
  email: Email,
  name: Name,
  initials: Initials,
  color: UserColor,
  role: Role.default('MEMBER'),
});
export type InviteUserBody = z.input<typeof InviteUserBody>;

/**
 * `PATCH /api/admin/users/:id`. `status: 'DISABLED'` désactive (et ferme les sessions),
 * `status: 'ACTIVE'` réactive (le statut redevient INVITED si la personne ne s'est jamais connectée).
 * Lot S : `email` ne se change que pour un membre qui ne s'est jamais connecté (faute de frappe dans une
 * invitation) : sinon 422 `EMAIL_LOCKED` ; adresse déjà prise : 409 `USER_ALREADY_EXISTS`.
 */
export const UpdateUserBody = z
  .object({
    email: Email.optional(),
    name: Name.optional(),
    initials: Initials.optional(),
    color: UserColor.optional(),
    role: Role.optional(),
    status: z.enum(['ACTIVE', 'DISABLED']).optional(),
  })
  .refine((body) => Object.values(body).some((value) => value !== undefined), {
    message: 'Aucune modification demandée',
  });
export type UpdateUserBody = z.input<typeof UpdateUserBody>;

export const UserIdParams = z.object({ id: Uuid });

/**
 * Lot S : `POST /api/admin/users/:id/merge { intoUserId }` fusionne le membre `:id` (le doublon, supprimé)
 * dans `intoUserId` (gardé) : toutes ses références passent au membre gardé, dans une transaction.
 * Règle : le doublon ne s'est jamais connecté, ou il est désactivé ; sinon 409 `MERGE_BOTH_LOGGED_IN`.
 */
export const MergeUserBody = z.object({ intoUserId: Uuid });
export type MergeUserBody = z.infer<typeof MergeUserBody>;

/** Réponse de la fusion : le membre gardé et le nombre de références déplacées par table. */
export const MergeUserResult = z.object({
  kept: User,
  removedUserId: Uuid,
  moved: z.record(z.string(), z.int().min(0)),
});
export type MergeUserResult = z.infer<typeof MergeUserResult>;

/**
 * `GET /api/members` (tout membre connecté, lot Z) : l'équipe active ou invitée, pour les listes de choix
 * (« Pour », filtres, « fait par »). Jamais d'adresse e-mail ni de statut.
 */
export const Member = z.object({
  id: Uuid,
  name: z.string(),
  initials: z.string(),
  color: z.string(),
  role: Role,
});
export type Member = z.infer<typeof Member>;
export const MemberList = z.array(Member);
